import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Users } from "lucide-react";
import { myCompanion, myCircles } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Peer review target selector — Companion or Circle only (random/friend-
 * by-email targeting was removed). Fetches the student's own Companion/
 * Circles, defaults to whichever exists, and reports the resolved
 * `{target_type, target_circle_id, ready}` via `onChange` on every change.
 * When the student has neither, renders a "join Community first" prompt
 * instead of a picker — `ready` stays false so the caller can disable
 * submission.
 */
export default function PeerReviewTargetPicker({ onChange }) {
  const { t } = useI18n();
  const [companion, setCompanion] = useState(null);
  const [circles, setCircles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [target, setTarget] = useState(null); // "companion" | "circle"
  const [circleId, setCircleId] = useState("");

  useEffect(() => {
    Promise.all([
      myCompanion().then((r) => r.companion).catch(() => null),
      myCircles().then((r) => r.circles || []).catch(() => []),
    ]).then(([comp, circ]) => {
      setCompanion(comp);
      setCircles(circ);
      setTarget(comp ? "companion" : circ.length ? "circle" : null);
      if (circ.length) setCircleId(circ[0].circle_id);
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!loaded) return;
    onChange({
      target_type: target,
      target_circle_id: target === "circle" ? circleId : null,
      ready: target === "companion" ? Boolean(companion) : target === "circle" ? Boolean(circleId) : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, circleId, loaded]);

  if (!loaded) return null;

  if (!companion && circles.length === 0) {
    return (
      <div data-testid="peer-target-none" className="text-xs text-[#4B5563] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2">
        {t("peer_target.join_prompt")} <Link to="/community" className="text-[#D97706] font-medium hover:underline">{t("peer_target.go_to_community")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="peer-target-picker">
      <div className="text-[11px] uppercase tracking-[0.16em] text-[#4B5563] mb-1.5">{t("peer_target.send_to")}</div>
      <div className="flex flex-wrap items-center gap-2">
        {companion && (
          <button type="button" data-testid="peer-target-companion" onClick={() => setTarget("companion")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              target === "companion" ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] text-[#1F2937] hover:border-[#064E3B]"
            }`}>
            <Heart className="w-3.5 h-3.5" /> {t("peer_target.quran_companion")}
          </button>
        )}
        {circles.length > 0 && (
          <button type="button" data-testid="peer-target-circle" onClick={() => setTarget("circle")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              target === "circle" ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] text-[#1F2937] hover:border-[#064E3B]"
            }`}>
            <Users className="w-3.5 h-3.5" /> {t("peer_target.circle")}
          </button>
        )}
      </div>
      {target === "circle" && circles.length > 0 && (
        <div className="mt-2" data-testid="peer-target-circle-row">
          <select data-testid="peer-target-circle-select" value={circleId} onChange={(e) => setCircleId(e.target.value)}
            className="w-full max-w-sm px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm">
            {circles.map((c) => <option key={c.circle_id} value={c.circle_id}>{c.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
